import React, { useEffect, useState } from 'react';
import {
  Container, Typography, Paper, Box, Button, Divider, useTheme, useMediaQuery, Card, CardContent,
  IconButton
} from '@mui/material';
import { Delete, Email, Person, Subject, AccessTime, Phone, Category as CategoryIcon } from '@mui/icons-material';
import api from '../../utils/api';
import { snackbar, successMessage } from '../../utils/showMessage';

const PAGE_SIZE = 10;

const SOURCE_TITLES = {
  web: 'پیام‌های تماس از سایت',
  app: 'پیام‌های تماس از اپلیکیشن',
};

const ContactMessages = ({ source = 'web' }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [messages, setMessages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [total, setTotal] = useState(0);

    useEffect(() => {
      setPage(1);
    }, [source]);

    const fetchMessages = async (pageNum = 1) => {
      setLoading(true);
      try {
        const res = await api.get(`/contact/messages?page=${pageNum}&limit=${PAGE_SIZE}&source=${source}`);
        if (res?.data?.data) {
          setMessages(res.data.data);
          setTotal(res.data.total || 0);
        } else {
          setMessages([]);
          setTotal(0);
        }
      } catch (e) {
        snackbar('خطا در بارگذاری پیام‌ها');
        setMessages([]);
        setTotal(0);
      } finally {
        setLoading(false);
      }
    };

    useEffect(() => {
      fetchMessages(page);
      // eslint-disable-next-line
    }, [page, source]);

    const handleDelete = async (id) => {
      if (!window.confirm('آیا این پیام حذف شود؟')) return;
      try {
        await api.delete(`/contact/messages/${id}`);
        successMessage('پیام حذف شد');
        fetchMessages(page);
      } catch (err) {
        snackbar('خطا در حذف پیام');
      }
    };

    return (
      <Container maxWidth="lg" sx={{ mt: isMobile ? 2 : 5, mb: 5, pt: isMobile ? 8 : 3 }}>
        <Typography variant={isMobile ? "h6" : "h5"} fontWeight="bold" color="primary" mb={3}>
          {SOURCE_TITLES[source] || 'پیام‌های تماس با ما'}
        </Typography>

        {loading ? (
          <Box textAlign="center" py={5}>
            <Typography color="textSecondary">در حال بارگذاری...</Typography>
          </Box>
        ) : messages.length === 0 ? (
          <Paper sx={{ p: 5, textAlign: 'center', borderRadius: 3, border: '1px dashed #ccc' }}>
            <Typography color="textSecondary">هیچ پیامی وجود ندارد.</Typography>
          </Paper>
        ) : (
          <>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {messages.map(m => (
                <Card key={m._id} sx={{ borderRadius: 4, boxShadow: '0 4px 12px rgba(0,0,0,0.05)', position: 'relative' }}>
                  <CardContent sx={{ p: 2 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Box sx={{ bgcolor: 'primary.lighter', p: 1, borderRadius: 2, color: 'primary.main' }}>
                          <Person fontSize="small" />
                        </Box>
                        <Typography variant="subtitle1" fontWeight="bold">
                          {m.fullName || m.name || 'کاربر ناشناس'}
                        </Typography>
                      </Box>
                      <IconButton color="error" onClick={() => handleDelete(m._id)} size="small" sx={{ bgcolor: 'error.lighter' }}>
                        <Delete fontSize="small" />
                      </IconButton>
                    </Box>
                    <Box display="flex" flexWrap="wrap" gap={2} mb={2}>
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <Email sx={{ fontSize: 16, color: 'text.secondary' }} />
                        <Typography variant="caption" color="text.secondary">{m.email || 'بدون ایمیل'}</Typography>
                      </Box>
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <Phone sx={{ fontSize: 16, color: 'text.secondary' }} />
                        <Typography variant="caption" color="text.secondary">{m.phone || 'بدون شماره'}</Typography>
                      </Box>
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <Subject sx={{ fontSize: 16, color: 'text.secondary' }} />
                        <Typography variant="caption" color="text.secondary">{m.subject || 'بدون موضوع'}</Typography>
                      </Box>
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <CategoryIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                        <Typography variant="caption" color="text.secondary">{m.category || 'بدون دسته‌بندی'}</Typography>
                      </Box>
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <AccessTime sx={{ fontSize: 16, color: 'text.secondary' }} />
                        <Typography variant="caption" color="text.secondary">{m.createdAt ? new Date(m.createdAt).toLocaleString('fa-IR') : ''}</Typography>
                      </Box>
                    </Box>
                    <Divider sx={{ mb: 2, borderStyle: 'dotted' }} />
                    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: 'grey.50', border: 'none' }}>
                      <Typography variant="body2" sx={{ lineHeight: 1.8, whiteSpace: 'pre-wrap' }}>
                        {m.message}
                      </Typography>
                    </Paper>
                  </CardContent>
                </Card>
              ))}
            </Box>
            {/* Pagination */}
            <Box display="flex" justifyContent="center" alignItems="center" mt={4} gap={2}>
              <Button variant="outlined" size="small" disabled={page === 1} onClick={() => setPage(page - 1)}>قبلی</Button>
              <Typography variant="body2">صفحه {page} از {Math.ceil(total / PAGE_SIZE) || 1}</Typography>
              <Button variant="outlined" size="small" disabled={page === Math.ceil(total / PAGE_SIZE) || total === 0} onClick={() => setPage(page + 1)}>بعدی</Button>
            </Box>
          </>
        )}
      </Container>
    );
  };

  export default ContactMessages;
